// Phase 40 Plan 02 Task 1 — Supabase client singleton. CONTEXT D-09. RESEARCH.md Pattern 1.
//
// Single instantiation per browser tab. Uses Vite-injected env vars at build time.
// PKCE flow + persistSession + autoRefreshToken match the v3.0 ADR-2 contract:
// the SDK manages the access/refresh token lifecycle in localStorage under
// `sb-<project-ref>-auth-token`, so the rest of the app reads the JWT via
// `getJwt()` rather than touching the SDK directly.
//
// All sign-in surfaces (SignInModal magic-link form + provider buttons,
// callback handler) consume the helpers exported here. UK English in any
// user-visible error strings.

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Build a benign no-op stand-in when env is unconfigured — keeps unit tests
// + dev-without-.env.local from crashing at import time. Real flows fail at
// the first sign-in attempt with a clear "auth not configured" error.
//
// flowType: 'implicit' rather than 'pkce'. PKCE requires the verifier to be
// in localStorage in the SAME browser context that clicks the email link —
// fragile when users send themselves a magic link and click it in their
// default mail client which may open in a different browser. Implicit flow
// puts the access_token in the URL fragment after Supabase verifies the
// magic-link token server-side, so detectSessionInUrl picks it up regardless
// of which tab/browser the verify hop landed in.
function makeClient() {
  const authConfig = {
    flowType: 'implicit',
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  };
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return createClient('https://unconfigured.supabase.co', 'unconfigured-anon-key', { auth: authConfig });
  }
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, { auth: authConfig });
}

export const supabase = makeClient();

/**
 * Kick off the OAuth redirect for the given provider. Returns the SDK's
 * `{ data, error }` shape — error is non-null on misconfiguration only;
 * the success path is a full-page redirect, so callers won't normally see
 * the resolved value.
 *
 * @param {'google' | 'apple' | 'facebook'} provider
 */
// Vite serves dev at /garden-planner/ and prod under VITE_BASE_PATH (/tend/),
// so the redirectTo must respect import.meta.env.BASE_URL — otherwise Supabase
// returns the user to /auth/callback at the project root, which 404s in dev
// and is only mounted at root in prod via the vercel.json rewrite. The
// trailing slash on BASE_URL ("/garden-planner/") is normalised to a single
// slash before "auth/callback".
const AUTH_REDIRECT = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/auth/callback`;

export async function signInWithProvider(provider) {
  return supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: AUTH_REDIRECT,
    },
  });
}

/**
 * Send a magic link to the supplied email. PKCE flow is honoured by the
 * client default `flowType: 'pkce'` above — Supabase issues a one-time code
 * the link consumes via `exchangeCodeForSession`.
 *
 * @param {string} email
 */
export async function signInWithMagicLink(email) {
  return supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: AUTH_REDIRECT,
    },
  });
}

/**
 * Send a 6-digit OTP code to the supplied email. Cross-context-safe: the
 * code can be entered in the same tab the user requested it from, sidestepping
 * the installed-PWA-vs-default-browser mismatch that breaks magic-link flows.
 *
 * Omitting `emailRedirectTo` tells Supabase to send the code-style email
 * template rather than the magic-link template.
 *
 * @param {string} email
 */
export async function signInWithEmailOtp(email) {
  return supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
    },
  });
}

/**
 * Verify a 6-digit OTP code. On success Supabase issues a session and the
 * SDK persists it to localStorage under `sb-<project-ref>-auth-token`, the
 * same shape as the magic-link flow — so AuthProvider's onAuthStateChange
 * subscriber picks it up without further wiring.
 *
 * @param {string} email
 * @param {string} token
 */
export async function verifyEmailOtp(email, token) {
  return supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  });
}

/**
 * Resolve the current access token from the active session. Returns null
 * when signed out. Used by `src/lib/ai.js` to attach `Authorization: Bearer`
 * to Worker calls.
 */
export async function getJwt() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

/** Sign out and clear the SDK's localStorage session keys. */
export async function signOut() {
  return supabase.auth.signOut();
}
